import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fetchBusinesses as fetchBusinessesFromApi } from '../lib/api/businesses';
import { useAuth } from './AuthContext';

const BusinessesContext = createContext({
  businesses: [],
  loading: true,
  error: null,
  refetch: () => Promise.resolve([]),
});

export const BusinessesProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const loadBusinesses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchBusinessesFromApi();
      if (isMountedRef.current) {
        setBusinesses(result);
        hasFetchedRef.current = true;
      }
      return result;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        setBusinesses([]);
        hasFetchedRef.current = false;
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!isAuthenticated) {
      hasFetchedRef.current = false;
      setBusinesses([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (hasFetchedRef.current) {
      return;
    }

    loadBusinesses().catch(() => {
      // errors handled by state
    });
  }, [authLoading, isAuthenticated, loadBusinesses]);

  const value = useMemo(
    () => ({
      businesses,
      loading,
      error,
      refetch: loadBusinesses,
    }),
    [businesses, loading, error, loadBusinesses],
  );

  return (
    <BusinessesContext.Provider value={value}>
      {children}
    </BusinessesContext.Provider>
  );
};

export const useBusinesses = () => useContext(BusinessesContext);

export default BusinessesContext;
